<template>
    <div class="max-w-4xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    {{ forum?.title || "Forum" }}
                </h1>
                <p
                    class="text-sm text-gray-500 dark:text-gray-400 mt-1"
                    v-if="forum"
                >
                    {{ forum.class_subject?.class?.name }} -
                    {{ forum.class_subject?.subject?.name }} ·
                    {{ forum.teacher?.user?.name }}
                </p>
            </div>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
            ></div>
        </div>

        <template v-if="!loading && forum">
            <!-- New Thread Composer -->
            <div
                v-if="canPost"
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
            >
                <div class="space-y-3">
                    <input
                        v-model="newThread.title"
                        type="text"
                        placeholder="Thread title"
                        class="form-input w-full"
                    />
                    <textarea
                        v-model="newThread.content"
                        rows="3"
                        placeholder="Write your discussion..."
                        class="form-input w-full"
                    ></textarea>
                    <div class="flex justify-end">
                        <button
                            @click="createThread"
                            :disabled="
                                posting ||
                                !newThread.title ||
                                !newThread.content
                            "
                            class="btn btn-primary disabled:opacity-50"
                        >
                            {{ posting ? "Posting..." : "Post Thread" }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Approval notice -->
            <div
                v-if="forum.require_approval && !isModerator"
                class="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-3 text-sm text-info-700 dark:text-info-300"
            >
                Posts in this forum require teacher approval before they are
                visible to everyone.
            </div>

            <!-- Threads -->
            <div
                v-if="threadsLoading"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <SkeletonLoader variant="cards" :rows="4" />
            </div>

            <EmptyState
                v-else-if="threads.length === 0"
                icon="MessagesSquareIcon"
                title="No threads yet"
                description="Be the first to start a discussion in this forum."
            />

            <div v-else class="space-y-3">
                <div
                    v-for="thread in threads"
                    :key="thread.id"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
                    @click="openThread(thread)"
                >
                    <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                            <div class="flex items-center gap-2 flex-wrap">
                                <span
                                    v-if="thread.is_pinned"
                                    class="inline-flex items-center text-xs font-semibold text-warning-600 dark:text-warning-400"
                                >
                                    <Pin class="w-3.5 h-3.5 mr-1" />
                                    Pinned
                                </span>
                                <span
                                    v-if="thread.is_locked"
                                    class="inline-flex items-center text-xs font-semibold text-gray-500"
                                >
                                    <Lock class="w-3.5 h-3.5 mr-1" />
                                    Locked
                                </span>
                                <span
                                    v-if="thread.status === 'draft'"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200"
                                >
                                    Pending Approval
                                </span>
                                <span
                                    v-else-if="thread.status === 'hidden'"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Hidden
                                </span>
                                <h2
                                    class="text-sm font-semibold text-gray-900 dark:text-white truncate"
                                >
                                    {{ thread.title }}
                                </h2>
                            </div>
                            <p
                                class="text-sm text-gray-500 dark:text-gray-400 truncate mt-1"
                            >
                                {{ thread.excerpt || thread.content }}
                            </p>
                            <p
                                class="text-xs text-gray-400 dark:text-gray-500 mt-1"
                            >
                                {{ thread.user?.name || "-" }} ·
                                {{ formatDate(thread.created_at) }}
                            </p>
                        </div>
                        <div
                            class="flex-shrink-0 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400"
                        >
                            <span class="inline-flex items-center gap-1">
                                <Heart
                                    class="w-3.5 h-3.5"
                                    :class="
                                        thread.is_liked_by_me
                                            ? 'text-danger-500 fill-danger-500'
                                            : ''
                                    "
                                />
                                {{ thread.like_count }}
                            </span>
                            <span class="inline-flex items-center gap-1">
                                <Eye class="w-3.5 h-3.5" />
                                {{ thread.view_count }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <BasePagination
                v-if="threads.length > 0"
                :pagination="meta"
                :per-page="perPage"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Pin, Lock, Eye, Heart } from "@lucide/vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import BasePagination from "@/components/BasePagination.vue";
import forumApi, { Forum, ForumPost } from "@/api/forum";
import { useAuthStore } from "@/stores/authStore";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const forumId = route.params.id as string;

const forum = ref<Forum | null>(null);
const threads = ref<ForumPost[]>([]);
const meta = ref<any>(null);
const loading = ref(false);
const threadsLoading = ref(false);
const posting = ref(false);
const perPage = ref(15);
const page = ref(1);

const newThread = ref({ title: "", content: "" });

const isModerator = computed(
    () =>
        authStore.hasAnyRole(["administrator", "super-administrator"]) ||
        authStore.user?.id === forum.value?.teacher?.user?.id,
);

const canPost = computed(() => {
    if (!forum.value) return false;
    if (isModerator.value) return true;
    return forum.value.is_active && forum.value.allow_student_posts;
});

async function fetchForum() {
    loading.value = true;

    try {
        forum.value = await forumApi.getById(forumId);
    } catch (err: any) {
        console.error(err);
    } finally {
        loading.value = false;
    }
}

async function fetchThreads() {
    threadsLoading.value = true;

    try {
        const response = await forumApi.getThreads(forumId, {
            page: page.value,
            per_page: perPage.value,
        });

        threads.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        console.error(err);
    } finally {
        threadsLoading.value = false;
    }
}

async function createThread() {
    posting.value = true;

    try {
        const thread = await forumApi.createThread(forumId, {
            title: newThread.value.title,
            content: newThread.value.content,
        });

        newThread.value = { title: "", content: "" };
        await fetchThreads();
    } catch (err: any) {
        console.error(err);
    } finally {
        posting.value = false;
    }
}

function openThread(thread: ForumPost) {
    router.push(`/forums/${forumId}/threads/${thread.id}`);
}

function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function goToPage(newPage: number) {
    page.value = newPage;
    fetchThreads();
}

function handlePerPageChange(value: number) {
    perPage.value = value;
    page.value = 1;
    fetchThreads();
}

function goBack() {
    router.push("/forums");
}

onMounted(() => {
    fetchForum();
    fetchThreads();
});
</script>
